import { UseSearchReturn } from "@/hooks/useSearch";
import { ArrowRight, Calendar, Loader2, Search, Users } from "lucide-react";
import { useEffect, useState } from "react";
import SearchResultItem from "./SearchResultItem";

interface SearchResultsDropdownProps {
  searchState: UseSearchReturn;
  onClose: () => void;
  onStudentClick: (student: any) => void;
  onSessionClick: (session: any) => void;
}

export default function SearchResultsDropdown({
  searchState,
  onClose,
  onStudentClick,
  onSessionClick,
}: SearchResultsDropdownProps) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const {
    query,
    results,
    isLoading,
    isOpen,
    studentResults,
    sessionResults,
    hasResults,
  } = searchState;

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            handleResultClick(results[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleResultClick = (result: any) => {
    if (result.type === "student") {
      onStudentClick(result.data);
    } else {
      onSessionClick(result.data);
    }
    onClose();
  };

  const handleViewAllStudents = () => {
    // Navigate to students page
    window.location.href = "/students";
    onClose();
  };

  const handleViewAllSessions = () => {
    // Navigate to sessions page
    window.location.href = "/sessions";
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-2 max-h-96 overflow-hidden shadow-lg border border-gray-200 rounded-lg z-50 bg-white">
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-6">
            <Loader2 className="w-5 h-5 animate-spin text-gray-500 mr-2" />
            <span className="text-sm text-gray-600">Searching...</span>
          </div>
        ) : !hasResults ? (
          <div className="p-6 text-center">
            <Search className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              {query.trim().length < 2
                ? "Start typing to search"
                : "No results found"}
            </h3>
            <p className="text-xs text-gray-600">
              {query.trim().length < 2
                ? "Search for connected students, sessions, and more"
                : `Try different keywords or check spelling`}
            </p>
          </div>
        ) : (
          <div className="p-2">
            {/* Students Section */}
            {studentResults.length > 0 && (
              <div>
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-medium text-gray-900">
                      Students ({studentResults.length})
                    </h3>
                  </div>
                  {studentResults.length === 5 && (
                    <button
                      onClick={handleViewAllStudents}
                      className="flex items-center text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View all <ArrowRight className="w-3 h-3 ml-1" />
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {studentResults.map((result, index) => {
                    const globalIndex = index;
                    return (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={selectedIndex === globalIndex}
                        onClick={() => handleResultClick(result)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Separator */}
            {studentResults.length > 0 && sessionResults.length > 0 && (
              <hr className="my-3 border-gray-200" />
            )}

            {/* Sessions Section */}
            {sessionResults.length > 0 && (
              <div>
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-green-600" />
                    <h3 className="text-sm font-medium text-gray-900">
                      Sessions ({sessionResults.length})
                    </h3>
                  </div>
                  {sessionResults.length === 5 && (
                    <button
                      onClick={handleViewAllSessions}
                      className="flex items-center text-xs text-green-600 hover:text-green-700 font-medium"
                    >
                      View all <ArrowRight className="w-3 h-3 ml-1" />
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {sessionResults.map((result, index) => {
                    const globalIndex = studentResults.length + index;
                    return (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={selectedIndex === globalIndex}
                        onClick={() => handleResultClick(result)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-200 mt-3 pt-2 px-2">
              <p className="text-xs text-gray-500 text-center">
                Use ↑↓ to navigate, Enter to select, Esc to close
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
